/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosSeoFragment } from "@cosmos/hygraph-schema";
import { LocaleMessages } from "@epic-mw/i18n";
import { Metadata } from "next";

import { getAlternates } from "./alternates";
import { buildLocalizedUrl } from "./url.utils";

import type { LocalizedUrlOptions } from "./url.utils";

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const titleSuffixRegex = (titleSuffix?: string): RegExp =>
	new RegExp(`([\\|-]\\s*${escapeRegExp(titleSuffix ?? "")}\\s*)$`, "gi");

export const rootLayoutMetadata = {
	title: {
		template: `%s - ${process.env.APP_DEFAULT_TITLE_SUFFIX}`,
		// `default` is never run through `template`, so it has to already carry the suffix.
		// It is what Next renders for segments with no title of their own — most notably the
		// root `not-found.tsx`, which no `generateMetadata` covers.
		default: process.env.APP_DEFAULT_TITLE,
	},
	// tell Next to not normalize our URLs
	metadataBase: undefined,
};

export const postProcessMetaDataTitle = (
	title?: string | null,
	titleSuffix = process.env.APP_DEFAULT_TITLE_SUFFIX,
): string => {
	if (title) {
		// The suffix is re-appended by buildPageTitle.
		// Here we remove it to prevent double appending.
		// Also remove an accidental double hyphen
		return title
			.replace(titleSuffixRegex(titleSuffix?.trim() || ""), "")
			.replace(" -  - ", " - ")
			.replace(/\s*-\s*$/g, "")
			.trim();
	} else {
		// Let top level layout.tsx assign the DEFAULT_TITLE
		return "";
	}
};

/**
 * Builds the complete `<title>`, suffix included.
 *
 * We cannot lean on `rootLayoutMetadata.title.template` for this: Next only applies a
 * template to *nested* segments, so a `page.tsx` sitting next to the `layout.tsx` that
 * declares the template (every site's homepage since the `[lang]` segment was removed)
 * silently renders without the suffix. Resolving the title here keeps it correct no
 * matter where in the layout tree the route lives.
 *
 * Returns "" only when neither a title nor a suffix is available, so Next falls back to
 * `title.default`. If a suffix is configured but no usable title remains, the suffix itself
 * is returned rather than an empty string — there is still something meaningful to show.
 */
export const buildPageTitle = (title?: string | null, titleSuffix = process.env.APP_DEFAULT_TITLE_SUFFIX): string => {
	// Stripping the suffix leaves the space that preceded its separator behind, which the
	// old template-based flow turned into "Title  - Suffix".
	// A title falling back to APP_DEFAULT_TITLE (e.g. cosmos-website's env-derived default)
	// carries the env-level suffix even when a per-site titleSuffix is passed in, so both
	// have to be stripped before we re-append the one that actually applies here.

	const withoutSiteSuffix = postProcessMetaDataTitle(title, titleSuffix);
	const base = postProcessMetaDataTitle(withoutSiteSuffix, process.env.APP_DEFAULT_TITLE_SUFFIX).trim();
	const cleanSuffix = titleSuffix?.trim() || "";
	if (!cleanSuffix) {
		// No suffix configured: return whatever base we have (possibly "").
		return base;
	}
	// Nothing left to combine the suffix with (base is empty), or the base *is* the suffix
	// outright with no separator to strip (e.g. support-kws-website, whose title and suffix
	// are both "Support - Kids Web Services", or a differently-cased match of the suffix) —
	// either way, show the suffix on its own instead of duplicating or blanking the title.
	if (!base || base.toLowerCase() === cleanSuffix.toLowerCase()) {
		return cleanSuffix;
	}

	return `${base} - ${cleanSuffix}`;
};

export const getNextMetadata = (
	metaTags: CosmosSeoFragment,
	slug: string,
	locale: string,
	getMessages: (locale: string) => LocaleMessages,
	appDefaultImage: string,
	locales?: string[],
	defaultLocale?: string,
	// cosmos-website serves several properties from one app, so its suffix comes from
	// SiteConfig rather than the env var.
	titleSuffix = process.env.APP_DEFAULT_TITLE_SUFFIX,
	urlOptions?: LocalizedUrlOptions,
): Metadata => {
	const messages = getMessages(locale) || {};
	const { plainUrl: ogUrl } = buildLocalizedUrl(locale, slug, defaultLocale, urlOptions);

	const title = metaTags.title || messages["epic.app.meta.title"] || process.env.APP_DEFAULT_TITLE;
	const description = metaTags.description || messages["epic.app.meta.description"] || "";
	const socialTitle = metaTags.socialTitle || title;
	const resolvedTitle = buildPageTitle(title, titleSuffix);
	const defaultImage =
		appDefaultImage || "https://cdn2.unrealengine.com/epic-games-social-share-1200x675-7670432fc58a.png";

	return {
		// `absolute` because buildPageTitle already applied the suffix.
		title: resolvedTitle ? { absolute: resolvedTitle } : "",
		description,
		alternates: getAlternates(locale, slug, locales, defaultLocale, urlOptions),
		openGraph: {
			title: socialTitle,
			description: metaTags.socialDescription || description,
			url: ogUrl,
			type: "article",
			locale,
			siteName: titleSuffix,
			images: [
				{
					url: metaTags.openGraphImage?.url || defaultImage,
				},
			],
		},
		twitter: {
			title: socialTitle,
			description: metaTags.socialDescription || description,
			site: process.env.APP_TWITTER_SITE,
			images: [
				{
					url: metaTags.openGraphImage?.url || defaultImage,
				},
			],
		},
		robots: {
			index: metaTags.noIndex ? false : true,
			follow: metaTags.noFollow ? false : true,
			"max-image-preview": "large",
			googleBot: {
				index: metaTags.noIndex ? false : true,
				follow: metaTags.noFollow ? false : true,
				"max-image-preview": "large",
			},
		},
	};
};
